import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Settings, 
  Package, 
  Users, 
  Truck, 
  BarChart3, 
  ToggleLeft, 
  ToggleRight, 
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowLeft,
  Shield,
  CreditCard,
  Zap
} from 'lucide-react';

// API Service
import { modulesAPI, type Module, type ModuleStatusResponse } from '../../services/api';

const ModuleManagement: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const queryClient = useQueryClient();

  // Get current site ID
  const currentSiteId = localStorage.getItem('activeSiteId') || '';

  // Fetch module status
  const { data: moduleData, isLoading } = useQuery(
    ['modules', currentSiteId],
    () => modulesAPI.getModuleStatus(currentSiteId),
    {
      enabled: !!currentSiteId
    }
  );

  // Enable module mutation
  const enableModuleMutation = useMutation(
    ({ moduleName, settings }: { moduleName: string; settings?: any }) => 
      modulesAPI.enableModule(currentSiteId, moduleName, settings),
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        queryClient.invalidateQueries(['modules', currentSiteId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to enable module');
      }
    }
  );

  // Disable module mutation
  const disableModuleMutation = useMutation(
    (moduleName: string) => modulesAPI.disableModule(currentSiteId, moduleName),
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        queryClient.invalidateQueries(['modules', currentSiteId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to disable module');
      }
    }
  );

  // Update module settings mutation
  const updateSettingsMutation = useMutation(
    ({ moduleName, settings }: { moduleName: string; settings: any }) => 
      modulesAPI.updateModuleSettings(currentSiteId, moduleName, settings),
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        setShowSettingsModal(false);
        queryClient.invalidateQueries(['modules', currentSiteId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update settings');
      }
    }
  );

  const getModuleIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'bar-chart': BarChart3,
      'package': Package,
      'users': Users,
      'truck': Truck
    };
    const Icon = icons[iconName] || Settings;
    return <Icon className="w-6 h-6" />;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'analytics': 'bg-blue-100 text-blue-800',
      'operations': 'bg-green-100 text-green-800',
      'customer': 'bg-purple-100 text-purple-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getPricingBadge = (pricing: string) => {
    if (pricing === 'premium') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          <Zap className="w-3 h-3 mr-1" />
          Premium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Standard
      </span>
    );
  };

  const handleToggleModule = (module: Module) => {
    if (module.enabled) {
      disableModuleMutation.mutate(module.name);
    } else {
      enableModuleMutation.mutate({ moduleName: module.name });
    }
  };

  const handleOpenSettings = (module: Module) => {
    setSelectedModule(module);
    setShowSettingsModal(true);
  };

  const handleUpdateSettings = (settings: any) => {
    if (!selectedModule) return;
    
    updateSettingsMutation.mutate({
      moduleName: selectedModule.name,
      settings
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="h-6 w-px bg-gray-300"></div>
          <h1 className="text-2xl font-bold text-gray-900">Module Management</h1>
        </div>
        <p className="text-gray-600">
          Manage and configure modules for your site. Enable/disable features and customize settings.
        </p>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {moduleData?.data.modules.map((module) => (
          <div
            key={module.name}
            className={`bg-white rounded-lg shadow-sm border-2 ${
              module.enabled ? 'border-green-200' : 'border-gray-200'
            } hover:shadow-md transition-shadow`}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    module.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {getModuleIcon(module.icon)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{module.displayName}</h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getCategoryColor(module.category)}`}>
                      {module.category}
                    </span>
                  </div>
                </div>
                {getPricingBadge(module.pricing)}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">{module.description}</p>

              {/* Status */}
              <div className="flex items-center gap-2 mb-4">
                {module.enabled ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600">Enabled</span>
                    {module.configured && (
                      <>
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-blue-600">Configured</span>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">Disabled</span>
                  </>
                )}
              </div>

              {/* Role Requirement */}
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">
                  Requires {module.requiredRole === 'super_admin' ? 'Super Admin' : 'Admin'} access
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {module.canEnable && (
                  <button
                    onClick={() => handleToggleModule(module)}
                    disabled={enableModuleMutation.isLoading || disableModuleMutation.isLoading}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      module.enabled
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {module.enabled ? 'Disable' : 'Enable'}
                  </button>
                )}
                
                {module.enabled && (
                  <button
                    onClick={() => handleOpenSettings(module)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Settings
                  </button>
                )}

                {module.name === 'financialReports' && module.enabled && (
                  <Link
                    to="/admin/financial-reports"
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                  >
                    Open
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Settings Modal */}
      {showSettingsModal && selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedModule.displayName} Settings
              </h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* Module-specific settings would go here */}
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Module settings interface would be implemented here based on the specific module configuration.
              </p>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateSettings({})}
                  disabled={updateSettingsMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateSettingsMutation.isLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleManagement;
